import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getDriverApiBaseUrl, getApiBaseUrl } from '../../utils/utils';

const API_URL = getDriverApiBaseUrl();

const SmsMonitoring = () => {
  // Partners list (from CRM)
  const [partners, setPartners] = useState([]);

  // Filter state
  const [partnerCode, setPartnerCode] = useState('');
  const [awbNumber, setAwbNumber] = useState('');
  const [consigneePhone, setConsigneePhone] = useState('');
  const [smsStatus, setSmsStatus] = useState('');
  const [webhookStatus, setWebhookStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // Send new SMS state
  const [showSendModal, setShowSendModal] = useState(false);
  const [newSms, setNewSms] = useState({ partnerCode: '', awbNumber: '', consigneeName: '', consigneePhone: '', smsContent: '' });

  // Detail modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Status messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (partnerCode) params.append('partnerCode', partnerCode);
    if (awbNumber) params.append('awbNumber', awbNumber);
    if (consigneePhone) params.append('consigneePhone', consigneePhone);
    if (smsStatus) params.append('smsStatus', smsStatus);
    if (webhookStatus) params.append('webhookStatus', webhookStatus);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    params.append('page', page);
    params.append('size', pageSize);
    return params.toString();
  }, [partnerCode, awbNumber, consigneePhone, smsStatus, webhookStatus, fromDate, toDate, page, pageSize]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API_URL}/shypvdriverapp/api/sms-history/report?${params}`);
      const result = res.data;
      if (Array.isArray(result)) {
        setData(result);
        setTotalRows(result.length);
        setTotalPages(Math.ceil(result.length / pageSize));
      } else if (result.data) {
        setData(result.data.content || result.data);
        setTotalRows(result.data.totalElements || (Array.isArray(result.data) ? result.data.length : 0));
        setTotalPages(result.data.totalPages || Math.ceil((result.data.totalElements || 0) / pageSize));
      } else {
        setData([]);
        setTotalRows(0);
        setTotalPages(0);
      }
    } catch (err) {
      setError('Failed to fetch SMS data: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  }, [buildFilterParams, pageSize]);

  // Fetch partners from CRM on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const baseApiUrl = getApiBaseUrl();
        const response = await axios.get(`${baseApiUrl}/v1.0/partner/retrieve_list?page=0`);
        setPartners(response.data.data || []);
      } catch (err) {
        console.error('Error fetching partners:', err);
      }
    };
    fetchPartners();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const handleClearFilters = () => {
    setPartnerCode(''); setAwbNumber(''); setConsigneePhone('');
    setSmsStatus(''); setWebhookStatus(''); setFromDate(''); setToDate('');
    setPage(0);
    setTimeout(() => fetchData(), 100);
  };

  // Resend SMS
  const handleResend = async (row) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/shypvdriverapp/api/sms-history/resend`, {
        referenceId: row.referenceId
      });
      setSuccess(res.data.message || 'SMS resent successfully');
      fetchData();
    } catch (err) {
      setError('Resend failed: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  };

  // Edit & Resend
  const openEditModal = (row) => {
    setEditRecord(row);
    setEditContent(row.smsContent || '');
    setEditPhone(row.consigneePhone || '');
    setShowEditModal(true);
  };

  const handleEditAndResend = async () => {
    if (!editRecord) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/shypvdriverapp/api/sms-history/edit-and-resend`, {
        id: editRecord.id,
        referenceId: editRecord.referenceId,
        smsContent: editContent,
        consigneePhone: editPhone
      });
      setSuccess(res.data.message || 'SMS edited and resent');
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      setError('Edit & resend failed: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  };

  // Send New SMS
  const handleSendNew = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/shypvdriverapp/api/sms-history/send-new`, newSms);
      setSuccess(res.data.message || 'New SMS sent');
      setShowSendModal(false);
      setNewSms({ partnerCode: '', awbNumber: '', consigneeName: '', consigneePhone: '', smsContent: '' });
      fetchData();
    } catch (err) {
      setError('Send failed: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  };

  // Retry All Failed
  const handleRetryAllFailed = async () => {
    if (!window.confirm('Retry all failed SMS matching current filters?')) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = new URLSearchParams();
      if (partnerCode) params.append('partnerCode', partnerCode);
      if (awbNumber) params.append('awbNumber', awbNumber);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      const res = await axios.post(`${API_URL}/shypvdriverapp/api/sms-history/retry-failed?${params.toString()}`);
      const result = res.data;
      setSuccess(`Retried ${result.retried || 0} failed SMS`);
      fetchData();
    } catch (err) {
      setError('Retry failed: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  };

  // Download Excel
  const handleDownloadExcel = async () => {
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API_URL}/shypvdriverapp/api/sms-history/download/excel?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sms_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Excel download failed: ' + (err.response?.data?.message || err.message));
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API_URL}/shypvdriverapp/api/sms-history/download/pdf?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sms_report_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('PDF download failed: ' + (err.response?.data?.message || err.message));
    }
  };

  // View detail
  const viewDetail = (row) => {
    setSelectedRecord(row);
    setShowDetail(true);
  };

  // Helpers
  const statusBadge = (status) => {
    const colors = {
      SENT: { bg: "#dcfce7", color: "#166534" },
      FAILED: { bg: "#fee2e2", color: "#991b1b" },
      PENDING: { bg: "#fef3c7", color: "#92400e" },
      DELIVERED: { bg: "#dcfce7", color: "#166534" },
      REJECTED: { bg: "#fee2e2", color: "#991b1b" },
    };
    const c = colors[status] || { bg: "#f3f4f6", color: "#374151" };
    return (
      <span style={{ ...st.badge, backgroundColor: c.bg, color: c.color }}>{status || '-'}</span>
    );
  };

  const formatDate = (dt) => {
    if (!dt) return "-";
    return new Date(dt).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div style={st.container}>
      {/* Header */}
      <div style={st.header}>
        <div>
          <h1 style={st.title}>SMS Monitoring</h1>
          <p style={st.subtitle}>Monitor, retry, and manage SMS delivery to consignees</p>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button style={st.btnRefresh} onClick={fetchData}>
            <i className="fa fa-refresh" /> Refresh
          </button>
          <button style={st.btnPrimary} onClick={() => setShowSendModal(true)}>
            <i className="fa fa-plus" /> Send New SMS
          </button>
          <button style={st.btnDanger} onClick={handleRetryAllFailed} disabled={loading}>
            <i className="fa fa-repeat" /> Retry All Failed
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && <div style={st.alertError}>{error} <span style={st.closeBtn} onClick={() => setError("")}>&times;</span></div>}
      {success && <div style={st.alertSuccess}>{success} <span style={st.closeBtn} onClick={() => setSuccess("")}>&times;</span></div>}

      {/* Stats */}
      <div style={st.statsGrid}>
        <div style={{ ...st.statCard, borderLeft: "4px solid #4f7df3" }}>
          <div style={st.statIcon}><i className="fa fa-list-alt" style={{ color: "#4f7df3" }} /></div>
          <div><div style={st.statNumber}>{totalRows}</div><div style={st.statLabel}>Total Records</div></div>
        </div>
        <div style={{ ...st.statCard, borderLeft: "4px solid #10b981" }}>
          <div style={st.statIcon}><i className="fa fa-check-circle" style={{ color: "#10b981" }} /></div>
          <div><div style={st.statNumber}>{data.filter(r => r.smsStatus === 'SENT').length}</div><div style={st.statLabel}>Sent (Page)</div></div>
        </div>
        <div style={{ ...st.statCard, borderLeft: "4px solid #ef4444" }}>
          <div style={st.statIcon}><i className="fa fa-times-circle" style={{ color: "#ef4444" }} /></div>
          <div><div style={st.statNumber}>{data.filter(r => r.smsStatus === 'FAILED').length}</div><div style={st.statLabel}>Failed (Page)</div></div>
        </div>
        <div style={{ ...st.statCard, borderLeft: "4px solid #f59e0b" }}>
          <div style={st.statIcon}><i className="fa fa-clock-o" style={{ color: "#f59e0b" }} /></div>
          <div><div style={st.statNumber}>{data.filter(r => r.smsStatus === 'PENDING').length}</div><div style={st.statLabel}>Pending (Page)</div></div>
        </div>
        <div style={{ ...st.statCard, borderLeft: "4px solid #8b5cf6" }}>
          <div style={st.statIcon}><i className="fa fa-envelope-o" style={{ color: "#8b5cf6" }} /></div>
          <div><div style={st.statNumber}>{data.filter(r => r.webhookStatus === 'DELIVERED').length}</div><div style={st.statLabel}>Delivered (Page)</div></div>
        </div>
      </div>

      {/* Filters */}
      <div style={st.card}>
        <div style={st.cardHeader}><h3 style={st.cardTitle}><i className="fa fa-filter" style={{ marginRight: "8px" }} />Filters</h3></div>
        <div style={st.cardBody}>
          <div style={st.filterGrid}>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Partner</label>
              <select value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)} style={st.select}>
                <option value="">All Partners</option>
                {partners.map((p) => (
                  <option key={p.pid} value={p.pname}>{p.pname}</option>
                ))}
              </select>
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>AWB Number</label>
              <input type="text" value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} placeholder="e.g. AWB123" style={st.input} />
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Phone</label>
              <input type="text" value={consigneePhone} onChange={(e) => setConsigneePhone(e.target.value)} placeholder="971XXXXXXXXX" style={st.input} />
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>SMS Status</label>
              <select value={smsStatus} onChange={(e) => setSmsStatus(e.target.value)} style={st.select}>
                <option value="">All</option>
                <option value="SENT">SENT</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
              </select>
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Webhook Status</label>
              <select value={webhookStatus} onChange={(e) => setWebhookStatus(e.target.value)} style={st.select}>
                <option value="">All</option>
                <option value="DELIVERED">DELIVERED</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={st.input} />
            </div>
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={st.input} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
            <button style={st.btnPrimary} onClick={handleSearch} disabled={loading}>
              <i className="fa fa-search" /> Search
            </button>
            <button style={st.btnSecondary} onClick={handleClearFilters}>
              <i className="fa fa-eraser" /> Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div style={st.card}>
        <div style={st.cardHeader}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <h3 style={st.cardTitle}>SMS Records ({totalRows})</h3>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <button style={st.btnSmallEdit} onClick={handleDownloadExcel}>
                <i className="fa fa-file-excel-o" /> Excel
              </button>
              <button style={st.btnSmallRetry} onClick={handleDownloadPdf}>
                <i className="fa fa-file-pdf-o" /> PDF
              </button>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ ...st.select, width: "auto" }}>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={st.spinner}><div style={st.spinnerInner} /></div>
          ) : data.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>No SMS records found</div>
          ) : (
            <table style={st.table}>
              <thead>
                <tr style={st.tableHead}>
                  <th style={st.th}>ID</th>
                  <th style={st.th}>AWB</th>
                  <th style={st.th}>Partner</th>
                  <th style={st.th}>Consignee</th>
                  <th style={st.th}>Phone</th>
                  <th style={st.th}>Message</th>
                  <th style={st.th}>Status</th>
                  <th style={st.th}>Partner Status</th>
                  <th style={st.th}>Webhook</th>
                  <th style={st.th}>Error</th>
                  <th style={st.th}>Provider ID</th>
                  <th style={st.th}>Created At</th>
                  <th style={st.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id} style={st.tr}>
                    <td style={st.td}>{row.id}</td>
                    <td style={st.td}><span style={st.mono}>{row.awbNumber || "-"}</span></td>
                    <td style={st.td}>{row.partnerName || row.partnerCode || "-"}</td>
                    <td style={st.td}>{row.consigneeName || "-"}</td>
                    <td style={st.td}><span style={st.mono}>{row.consigneePhone || "-"}</span></td>
                    <td style={{ ...st.td, maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.smsContent || "-"}</td>
                    <td style={st.td}>{statusBadge(row.smsStatus)}</td>
                    <td style={st.td}>{row.partnerStatus || "-"}</td>
                    <td style={st.td}>{statusBadge(row.webhookStatus)}</td>
                    <td style={{ ...st.td, maxWidth: "180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.errorDetails || "-"}</td>
                    <td style={st.td}><span style={st.mono}>{row.serviceProviderId || "-"}</span></td>
                    <td style={st.td}>{formatDate(row.createdAt)}</td>
                    <td style={st.td}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button style={st.btnSmall} onClick={() => viewDetail(row)} title="View Details">
                          <i className="fa fa-eye" />
                        </button>
                        <button style={st.btnSmallEdit} onClick={() => openEditModal(row)} title="Edit & Resend">
                          <i className="fa fa-pencil" />
                        </button>
                        {(row.smsStatus === "FAILED" || row.smsStatus === "PENDING") && (
                          <button style={st.btnSmallRetry} onClick={() => handleResend(row)} title="Resend">
                            <i className="fa fa-repeat" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={st.pagination}>
            <button style={st.pageBtn} onClick={() => setPage(0)} disabled={page === 0}>
              <i className="fa fa-angle-double-left" />
            </button>
            <button style={st.pageBtn} onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
              <i className="fa fa-angle-left" />
            </button>
            <span style={st.pageInfo}>Page {page + 1} of {totalPages}</span>
            <button style={st.pageBtn} onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
              <i className="fa fa-angle-right" />
            </button>
            <button style={st.pageBtn} onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
              <i className="fa fa-angle-double-right" />
            </button>
          </div>
        )}
      </div>

      {/* ============ DETAIL MODAL ============ */}
      {showDetail && selectedRecord && (
        <div style={st.modalOverlay} onClick={() => setShowDetail(false)}>
          <div style={st.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h3 style={st.modalTitle}>SMS Detail #{selectedRecord.id}</h3>
              <button style={st.modalClose} onClick={() => setShowDetail(false)}>&times;</button>
            </div>
            <div style={st.modalBody}>
              <div style={st.detailGrid}>
                <div style={st.detailRow}><span style={st.detailLabel}>AWB Number:</span><span style={st.mono}>{selectedRecord.awbNumber || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Partner:</span><span>{selectedRecord.partnerName || selectedRecord.partnerCode || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Partner Code:</span><span>{selectedRecord.partnerCode || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Consignee:</span><span>{selectedRecord.consigneeName || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Phone:</span><span style={st.mono}>{selectedRecord.consigneePhone || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>SMS Status:</span>{statusBadge(selectedRecord.smsStatus)}</div>
                <div style={st.detailRow}><span style={st.detailLabel}>Webhook Status:</span>{statusBadge(selectedRecord.webhookStatus)}</div>
                <div style={st.detailRow}><span style={st.detailLabel}>Reference ID:</span><span style={st.mono}>{selectedRecord.referenceId || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Provider ID:</span><span style={st.mono}>{selectedRecord.serviceProviderId || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Amount:</span><span>{selectedRecord.amount || "-"}</span></div>
                <div style={st.detailRow}><span style={st.detailLabel}>Created At:</span><span>{formatDate(selectedRecord.createdAt)}</span></div>
              </div>
              {selectedRecord.smsContent && (
                <div style={{ marginTop: "16px" }}>
                  <div style={st.detailLabel}>SMS Content:</div>
                  <pre style={st.pre}>{selectedRecord.smsContent}</pre>
                </div>
              )}
              {selectedRecord.errorDetails && (
                <div style={{ marginTop: "16px" }}>
                  <div style={st.detailLabel}>Error Details:</div>
                  <pre style={st.pre}>{selectedRecord.errorDetails}</pre>
                </div>
              )}
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button style={st.btnSmallEdit} onClick={() => { setShowDetail(false); openEditModal(selectedRecord); }}>
                  <i className="fa fa-pencil" /> Edit & Resend
                </button>
                {(selectedRecord.smsStatus === "FAILED" || selectedRecord.smsStatus === "PENDING") && (
                  <button style={st.btnSmallRetry} onClick={() => { setShowDetail(false); handleResend(selectedRecord); }}>
                    <i className="fa fa-repeat" /> Resend
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ EDIT & RESEND MODAL ============ */}
      {showEditModal && (
        <div style={st.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={st.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h3 style={st.modalTitle}>Edit & Resend SMS</h3>
              <button style={st.modalClose} onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div style={st.modalBody}>
              <div style={st.detailRow}>
                <span style={st.detailLabel}>AWB: {editRecord?.awbNumber || "-"}</span>
              </div>
              <div style={st.editGrid}>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Phone Number</label>
                  <input type="text" style={st.input} value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                </div>
              </div>
              <div style={{ ...st.filterGroup, marginTop: "12px" }}>
                <label style={st.filterLabel}>SMS Content</label>
                <textarea style={st.textarea} value={editContent} onChange={e => setEditContent(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button style={st.btnPrimary} onClick={handleEditAndResend} disabled={loading}>
                  <i className="fa fa-send" /> Edit & Send
                </button>
                <button style={st.btnSecondary} onClick={() => setShowEditModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ SEND NEW SMS MODAL ============ */}
      {showSendModal && (
        <div style={st.modalOverlay} onClick={() => setShowSendModal(false)}>
          <div style={st.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h3 style={st.modalTitle}>Send New SMS</h3>
              <button style={st.modalClose} onClick={() => setShowSendModal(false)}>&times;</button>
            </div>
            <div style={st.modalBody}>
              <div style={st.editGrid}>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Partner *</label>
                  <select style={st.select} value={newSms.partnerCode} onChange={e => setNewSms({ ...newSms, partnerCode: e.target.value })}>
                    <option value="">Select Partner</option>
                    {partners.map((p) => (
                      <option key={p.pid} value={p.pname}>{p.pname}</option>
                    ))}
                  </select>
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>AWB Number</label>
                  <input type="text" style={st.input} placeholder="e.g. AWB12345" value={newSms.awbNumber} onChange={e => setNewSms({ ...newSms, awbNumber: e.target.value })} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Consignee Name</label>
                  <input type="text" style={st.input} placeholder="Customer name" value={newSms.consigneeName} onChange={e => setNewSms({ ...newSms, consigneeName: e.target.value })} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Phone Number *</label>
                  <input type="text" style={st.input} placeholder="971XXXXXXXXX" value={newSms.consigneePhone} onChange={e => setNewSms({ ...newSms, consigneePhone: e.target.value })} />
                </div>
              </div>
              <div style={{ ...st.filterGroup, marginTop: "12px" }}>
                <label style={st.filterLabel}>SMS Content *</label>
                <textarea style={st.textarea} placeholder="Enter SMS message..." value={newSms.smsContent} onChange={e => setNewSms({ ...newSms, smsContent: e.target.value })} />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button style={st.btnPrimary} onClick={handleSendNew} disabled={loading}>
                  <i className="fa fa-send" /> Send SMS
                </button>
                <button style={st.btnSecondary} onClick={() => setShowSendModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ STYLES (matches Emirates Scan Monitor) ============
const st = {
  container: { padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "12px" },
  title: { fontSize: "24px", fontWeight: "700", color: "#1e293b", margin: 0 },
  subtitle: { fontSize: "14px", color: "#64748b", margin: "4px 0 0" },

  // Cards
  card: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px", border: "1px solid #e2e8f0" },
  cardHeader: { padding: "14px 20px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc", borderRadius: "12px 12px 0 0" },
  cardTitle: { fontSize: "15px", fontWeight: "600", color: "#334155", margin: 0 },
  cardBody: { padding: "20px" },

  // Stats
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "16px", marginBottom: "20px" },
  statCard: { backgroundColor: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "16px" },
  statIcon: { fontSize: "28px" },
  statNumber: { fontSize: "28px", fontWeight: "700", color: "#1e293b" },
  statLabel: { fontSize: "13px", color: "#64748b", marginTop: "2px" },

  // Filters
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" },
  filterGroup: { display: "flex", flexDirection: "column", gap: "4px" },
  filterLabel: { fontSize: "12px", fontWeight: "600", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" },
  input: { padding: "9px 12px", fontSize: "14px", border: "1px solid #d1d5db", borderRadius: "8px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" },
  select: { padding: "9px 12px", fontSize: "14px", border: "1px solid #d1d5db", borderRadius: "8px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "12px", fontSize: "14px", border: "1px solid #d1d5db", borderRadius: "8px", minHeight: "120px", resize: "vertical", outline: "none", boxSizing: "border-box" },

  // Buttons
  btnPrimary: { padding: "10px 20px", fontSize: "14px", fontWeight: "500", backgroundColor: "#4f7df3", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnSecondary: { padding: "10px 20px", fontSize: "14px", fontWeight: "500", backgroundColor: "#e2e8f0", color: "#475569", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnRefresh: { padding: "10px 16px", fontSize: "14px", fontWeight: "500", backgroundColor: "#fff", color: "#475569", border: "1px solid #d1d5db", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnDanger: { padding: "10px 16px", fontSize: "14px", fontWeight: "500", backgroundColor: "#ef4444", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnSmall: { padding: "6px 10px", fontSize: "12px", backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer" },
  btnSmallEdit: { padding: "6px 10px", fontSize: "12px", backgroundColor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },
  btnSmallRetry: { padding: "6px 10px", fontSize: "12px", backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },

  // Table
  table: { width: "100%", borderCollapse: "collapse" },
  tableHead: { backgroundColor: "#f8fafc" },
  th: { padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  td: { padding: "12px 16px", fontSize: "13px", color: "#334155" },
  mono: { fontFamily: "monospace", fontSize: "12px" },

  // Badge
  badge: { padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "600", display: "inline-block" },

  // Pagination
  pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "16px" },
  pageBtn: { padding: "8px 12px", fontSize: "14px", backgroundColor: "#fff", color: "#475569", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" },
  pageInfo: { fontSize: "14px", color: "#475569", margin: "0 8px" },

  // Alerts
  alertError: { padding: "12px 16px", backgroundColor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: "8px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  alertSuccess: { padding: "12px 16px", backgroundColor: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: "8px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { cursor: "pointer", fontSize: "18px", fontWeight: "bold", paddingLeft: "12px" },

  // Modal
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modalContent: { backgroundColor: "#fff", borderRadius: "16px", width: "90%", maxWidth: "700px", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalHeader: { padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalTitle: { fontSize: "18px", fontWeight: "600", color: "#1e293b", margin: 0 },
  modalClose: { fontSize: "24px", background: "none", border: "none", cursor: "pointer", color: "#64748b" },
  modalBody: { padding: "24px" },

  // Detail
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  detailRow: { display: "flex", flexDirection: "column", gap: "2px" },
  detailLabel: { fontSize: "12px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" },
  editGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  pre: { backgroundColor: "#f1f5f9", padding: "12px", borderRadius: "8px", fontSize: "12px", fontFamily: "monospace", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: "200px", margin: "4px 0 0" },

  // Spinner
  spinner: { display: "flex", justifyContent: "center", padding: "40px" },
  spinnerInner: { width: "36px", height: "36px", border: "3px solid #e2e8f0", borderTopColor: "#4f7df3", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};

export default SmsMonitoring;
